import { useState, type CSSProperties, type ReactNode } from 'react'
import { cn } from '../lib/utils'
import { fabGlassClass } from '../lib/glass'

// A floating option chip (the schedule FABs, Home's crew filter). It's always a
// pill-shaped button; `showLabel` animates it between an icon-only circle (h-12 square, label track collapsed to
// 0fr) and a full-text pill (label track 1fr). Animating the grid track — rather
// than an auto width — is what lets the icon smoothly expand into the label. The
// icon sits on the right (text grows leftward), so every chip's icon — and the
// trigger's — stays on one vertical line regardless of label width.
export function OptionChip({
  icon,
  label,
  onClick,
  title,
  active,
  showLabel = true,
  testId,
  style,
  className,
}: {
  icon: ReactNode
  label: ReactNode
  onClick: () => void
  /** Accessible label / tooltip; also the a11y name when the pill is icon-only. */
  title?: string
  active?: boolean
  /** Icon + text pill (true) vs. icon-only circle (false). */
  showLabel?: boolean
  testId?: string
  style?: CSSProperties
  className?: string
}) {
  const [tapping, setTapping] = useState(false)
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => {
        setTapping(true)
        onClick()
      }}
      onAnimationEnd={() => setTapping(false)}
      title={title}
      aria-label={title}
      style={style}
      className={cn(
        // 13px padding + a 20px icon + 2px border = a 48px (h-12) circle collapsed,
        // matching the icon-only trigger's w-12. (h-12's border-box already nets 48.)
        // Fades read --fab-opacity on the chip itself: opacity on an ancestor would
        // cut the backdrop blur off mid-fade, then snap it back on at full opacity.
        'inline-flex shrink-0 items-center h-12 px-[13px] rounded-full cursor-pointer text-sm font-medium opacity-(--fab-opacity) transition-[color,background-color,border-color,opacity] duration-300',
        fabGlassClass,
        // Selected mirrors the nav's active tab: a primary tint layered over the glass.
        // The tint is a background-image so it stacks on the glass's dark fill — as a
        // background-color it would replace that fill and wash out over photos.
        active
          ? 'bg-linear-to-b from-primary/20 to-primary/20 text-primary border-primary/40 hover:from-primary/25 hover:to-primary/25'
          : 'text-foreground hover:bg-card/90',
        tapping && 'animate-btn-pop',
        className,
      )}
    >
      <span
        className={cn(
          // min-w-0 lets this flex item shrink below the label's min-content, so
          // grid-cols-[0fr] can actually collapse to a clean circle.
          'grid min-w-0 transition-[grid-template-columns] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
          showLabel ? 'grid-cols-[1fr]' : 'grid-cols-[0fr]',
        )}
      >
        <span
          className={cn(
            'min-w-0 overflow-hidden whitespace-nowrap transition-opacity duration-200',
            showLabel ? 'opacity-100' : 'opacity-0',
          )}
        >
          {label}
        </span>
      </span>
      {/* Gap lives on the icon (not the label) so it disappears with the label —
          padding on the clipped label would keep the collapsed pill 8px too wide. */}
      <span
        className={cn(
          'shrink-0 transition-[margin] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
          showLabel ? 'ml-2' : 'ml-0',
        )}
      >
        {icon}
      </span>
    </button>
  )
}
